import React, { useEffect, useRef, useState } from "react"
import { SearchFoodsContent, SearchFoodsWrapper } from "./styled"
import Header from "./Header"
import SearchAutocomplete from "components/search/SearchAutocomplete"
import { useSearchFoods, useSuggestedSearchTexts } from "../hooks"
import { FlexBox } from "components/Common/FlexBox"
import { useSuggestedMeals } from "features/meal/hooks/useSuggestedMeals"
import RecentMeals from "./RecentMeals"
import SuggestedSearchTexts from "components/search/SuggestedSearchTexts"
import SearchFoodResults from "components/search/SearchFoodResults"
import useLogMealFromFood from "features/meal/hooks/logMeal/useLogMealFromFood"
import { MealType, SearchFoodResultType } from "model/food/types"
import FoodDetailsSection from "./FoodDetailsSection"
import MealDetails from "components/mealDetails/MealDetails"
import { useSetMealTypeParams } from "features/meal/hooks"

enum IDisplayContent {
  RecentMeals = "Recent-meals",
  SuggestedSearchText = "Suggested-search-text",
  SearchResults = "Search-results",
  FoodDetails = "Food-details",
  MealDetails = "Meal-details",
}

const SearchFoods = () => {
  const [displayContent, setDisplayContent] = React.useState<IDisplayContent>(
    IDisplayContent.RecentMeals,
  )

  const {
    suggestedMeals,
    isNoSuggestedMeals,
    loadMore,
    getSuggestedMealsLoading,
    hasMore,
  } = useSuggestedMeals()

  const searchResultRef = useRef<HTMLDivElement>(null)
  const [searchResultScrollTop, setSearchResultScrollTop] = useState(0)

  useEffect(() => {
    if (
      searchResultRef.current &&
      displayContent === IDisplayContent.SearchResults
    ) {
      searchResultRef.current.scrollTop = searchResultScrollTop
    }
  }, [searchResultScrollTop, displayContent])

  const [foodDetails, setFoodDetails] = useState<SearchFoodResultType | null>(
    null,
  )

  const recentMealsRef = useRef<HTMLDivElement>(null)
  const [recentMealsScrollTop, setRecentMealsScrollTop] = useState(0)

  const { mealTypeValue } = useSetMealTypeParams()

  useEffect(() => {
    if (
      recentMealsRef.current &&
      displayContent === IDisplayContent.RecentMeals
    ) {
      recentMealsRef.current.scrollTop = recentMealsScrollTop
    }
  }, [recentMealsScrollTop, displayContent])

  const [mealDetails, setMealDetails] = useState<MealType | null>()

  const onSelectFood = (food: SearchFoodResultType) => {
    setFoodDetails(food)
    setDisplayContent(IDisplayContent.FoodDetails)
    setSearchResultScrollTop(searchResultRef.current?.scrollTop || 0)
  }

  const onCloseFoodDetails = () => {
    setFoodDetails(null)
    setDisplayContent(IDisplayContent.SearchResults)
  }

  const onSelectMeal = (meal: MealType) => {
    setMealDetails({ ...meal, type: mealTypeValue })
    setDisplayContent(IDisplayContent.MealDetails)
    setRecentMealsScrollTop(recentMealsRef.current?.scrollTop || 0)
  }

  const onCloseMealDetails = () => {
    setMealDetails(null)
    setDisplayContent(IDisplayContent.RecentMeals)
  }

  const {
    keyword,
    suggestedSearchTexts,
    handleKeywordChange,
    isNoSuggestedSearchTexts,
  } = useSuggestedSearchTexts()

  const {
    searchFoodsResults,
    handleSearchFoods,
    searchFoodsLoading,
    searchSession,
    noSearchedFoodFounded,
  } = useSearchFoods()

  const { handleLogMealFromFood } = useLogMealFromFood()

  if (displayContent === IDisplayContent.FoodDetails && foodDetails) {
    return <FoodDetailsSection food={foodDetails} onBack={onCloseFoodDetails} />
  }

  if (displayContent === IDisplayContent.MealDetails && mealDetails) {
    return <MealDetails mealDetails={mealDetails} onBack={onCloseMealDetails} />
  }

  return (
    <SearchFoodsWrapper>
      <Header />
      <FlexBox style={{ padding: "6px var(--app-horizontal-padding)" }}>
        <SearchAutocomplete
          visible={keyword !== ""}
          onFocus={() => {}}
          placeholder="Search"
          value={keyword}
          onChange={(keyword) => {
            handleKeywordChange(keyword)
            if (
              keyword &&
              displayContent !== IDisplayContent.SuggestedSearchText
            ) {
              setDisplayContent(IDisplayContent.SuggestedSearchText)
            }

            if (!keyword && displayContent !== IDisplayContent.RecentMeals) {
              setDisplayContent(IDisplayContent.RecentMeals)
            }
          }}
          onClose={() => {
            handleKeywordChange("")
            setDisplayContent(IDisplayContent.RecentMeals)
          }}
          onBlur={() => {}}
          onSearch={() => {
            const forceReset = true
            handleSearchFoods({
              nextKeyword: keyword,
              forceReset,
            })
            setDisplayContent(IDisplayContent.SearchResults)
          }}
        />
      </FlexBox>

      <SearchFoodsContent>
        {IDisplayContent.RecentMeals === displayContent && (
          <RecentMeals
            meals={suggestedMeals}
            isNoSuggestedMeals={isNoSuggestedMeals}
            loadMore={loadMore}
            loading={getSuggestedMealsLoading}
            hasMore={hasMore}
            onSelectMeal={onSelectMeal}
            recentMealsRef={recentMealsRef}
          />
        )}

        {displayContent === IDisplayContent.SuggestedSearchText && (
          <div style={{ padding: "0 var(--app-horizontal-padding)" }}>
            <SuggestedSearchTexts
              isNoSuggestedSearchTexts={isNoSuggestedSearchTexts}
              suggestedTexts={suggestedSearchTexts}
              keyword={keyword}
              onSelect={(keyword) => {
                const forceReset = true
                handleSearchFoods({ nextKeyword: keyword, forceReset })
                setDisplayContent(IDisplayContent.SearchResults)
              }}
            />
          </div>
        )}

        {displayContent === IDisplayContent.SearchResults && (
          <SearchFoodResults
            headerStyle={{
              padding: "8px var(--app-horizontal-padding)",
              fontSize: "14px",
              fontWeight: 700,
              lineHeight: "22px",
            }}
            contentRef={searchResultRef}
            onAddFood={handleLogMealFromFood}
            onSelectFood={onSelectFood}
            noSearchedFoodFounded={noSearchedFoodFounded}
            foods={searchFoodsResults}
            onLoad={() => handleSearchFoods({ nextKeyword: keyword })}
            loading={searchFoodsLoading}
            hasMore={!!searchSession}
            keyword={keyword}
            hasCreateLink={false}
          />
        )}
      </SearchFoodsContent>
    </SearchFoodsWrapper>
  )
}

export default SearchFoods
